import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { scrollToHash, CONTACT, EASE } from "./shared";

const LINKS = [
  { label: "Services", hash: "#services" },
  { label: "Entretien PPE", to: "/entretien-immeubles-ppe" },
  { label: "Nettoyage", to: "/nettoyage-professionnel" },
  { label: "Haute pression", to: "/nettoyage-haute-pression" },
  { label: "Petits travaux", to: "/travaux-entretien" },
  { label: "Réalisations", to: "/realisations" },
  { label: "À propos", to: "/a-propos" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const solid = scrolled || location.pathname !== "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (l) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (l.to) {
      navigate(l.to);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => window.__lenis?.scrollTo(l.hash, { offset: -100 }), 450);
    } else {
      scrollToHash(l.hash)();
    }
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          solid ? "bg-white/85 backdrop-blur-xl border-b border-nuit/10" : "bg-gradient-to-b from-nuit/70 via-nuit/25 to-transparent"
        }`}
        data-testid="main-nav"
      >
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-3 lg:px-8">
          <a href="/" onClick={(e) => { e.preventDefault(); setOpen(false); navigate("/"); window.__lenis?.scrollTo(0, { immediate: true }); }} className="flex shrink-0 items-center lg:mr-8" data-testid="nav-logo">
            <img
              src={solid ? "/assets/logo-light.webp" : "/assets/logo-dark.webp"}
              alt="Rosset Conciergerie & Services — conciergerie et entretien d'immeubles à Sion, Valais"
              className="h-16 w-auto transition-all duration-500 sm:h-20 xl:h-24"
            />
          </a>

          <nav className="hidden items-center gap-4 xl:flex">
            {LINKS.map((l) => (
              <a
                key={l.label}
                href={l.to || l.hash}
                onClick={go(l)}
                data-testid={`nav-link-${(l.to || l.hash).replace(/^[#/]+/, "")}`}
                className={`whitespace-nowrap text-xs font-medium tracking-wide transition-colors duration-300 hover:text-lagoon ${
                  solid ? "text-nuit/80" : "text-white/85"
                }`}
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={CONTACT.phoneHref}
              data-testid="nav-call-button"
              className={`hidden items-center gap-2 whitespace-nowrap text-xs font-semibold 2xl:flex transition-colors duration-300 ${
                solid ? "text-nuit" : "text-white"
              }`}
            >
              <Phone className="h-4 w-4" /> {CONTACT.phone}
            </a>
            <a
              href="/contact"
              onClick={go({ to: "/contact" })}
              data-testid="nav-cta-devis"
              className="hidden whitespace-nowrap rounded-full bg-lagoon px-5 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-nuit transition-all duration-300 hover:bg-white hover:shadow-lg md:block"
            >
              Demander un devis
            </a>
            <button
              onClick={() => setOpen(!open)}
              data-testid="nav-menu-toggle"
              aria-label="Menu"
              className={`xl:hidden ${solid ? "text-nuit" : "text-white"}`}
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-nuit/97 backdrop-blur-md xl:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex h-full flex-col justify-center gap-2 px-8 pb-24">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.label}
                  href={l.to || l.hash}
                  onClick={go(l)}
                  data-testid={`mobile-nav-link-${(l.to || l.hash).replace(/^[#/]+/, "")}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i + 0.1, duration: 0.6, ease: EASE }}
                  className="border-b border-white/10 py-4 font-display text-2xl font-medium text-white"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.a
                href="/contact"
                onClick={go({ to: "/contact" })}
                data-testid="mobile-nav-cta-devis"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6, ease: EASE }}
                className="mt-8 inline-block w-fit rounded-full bg-lagoon px-8 py-4 text-sm font-bold uppercase tracking-[0.12em] text-nuit"
              >
                Demander un devis
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
